import { useMemo, useState } from 'react';
import { Box, Flex, Text, Input, Grid, chakra } from '@chakra-ui/react';
import { VolunteersTabs } from '@/pages/Volunteers/components';
import { Search, Building2, MapPin } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import {
  filterMarketplaceEvents,
  filterMarketplaceOrganizations,
} from '@/hooks/useMarketplaceOrganizations';
import type { MarketplaceOrganization } from '@/types/marketplace';
import type { Organization } from '@/types/events';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import { EventImage } from '@/components/common/EventImage';
import { formatDateRange } from '@/utils/dateFormat';
import { getEventJoinCta } from '@/utils/eventJoinCta';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/ui/Toast/Toast';
import { MarketplaceOrganizationProfileModal } from './MarketplaceOrganizationProfileModal';

interface MarketplaceSectionProps {
  events: Organization[];
  organizations: MarketplaceOrganization[];
  loadingEvents: boolean;
  loadingOrganizations: boolean;
  volunteerUserId: string | null;
  onEventPress: (event: Organization) => void;
  onJoinOrganization: (organizationId: string) => Promise<{ ok: boolean; message?: string }>;
}

function orgDisplayName(o: MarketplaceOrganization): string {
  const n = [o.first_name, o.last_name].filter(Boolean).join(' ').trim();
  return n || 'Organization';
}

/** Same row height for Events (button) and Organizations (flex); centers content vertically */
const MARKETPLACE_ROW_MIN_H = '5.5rem'; // 88px — aligns both columns

const marketplaceTabs = [
  { id: 'events', label: 'Events' },
  { id: 'organizations', label: 'Organizations' },
] as const;

type MarketplaceTab = (typeof marketplaceTabs)[number]['id'];

export function MarketplaceSection({
  events,
  organizations,
  loadingEvents,
  loadingOrganizations,
  volunteerUserId,
  onEventPress,
  onJoinOrganization,
}: MarketplaceSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [joiningOrgId, setJoiningOrgId] = useState<string | null>(null);
  const [selectedOrgForProfile, setSelectedOrgForProfile] = useState<MarketplaceOrganization | null>(null);
  const [activeTab, setActiveTab] = useState<MarketplaceTab>('events');
  const { showToast, hideToast, toast } = useToast();

  const filteredEvents = useMemo(
    () => filterMarketplaceEvents(events, debouncedQuery),
    [events, debouncedQuery]
  );
  const filteredOrgs = useMemo(
    () => filterMarketplaceOrganizations(organizations, debouncedQuery),
    [organizations, debouncedQuery]
  );

  const handleJoinOrg = async (orgId: string) => {
    setJoiningOrgId(orgId);
    try {
      const r = await onJoinOrganization(orgId);
      if (!r.ok && r.message) showToast(r.message, 'error');
      else if (r.ok) showToast('Membership request sent.', 'success');
    } finally {
      setJoiningOrgId(null);
    }
  };

  return (
    <Box as="section" my="6" animation="fadeIn 0.3s ease-in-out">
      <Text
        as="h2"
        m="0"
        mb="3"
        fontSize={{ base: 'lg', md: 'xl' }}
        fontWeight="bold"
        color="volyx.text"
        fontFamily="body"
        letterSpacing="wide"
      >
        Marketplace
      </Text>

      <Flex
        align="center"
        bg="rgba(255,255,255,0.95)"
        border="1px solid"
        borderColor="volyx.mediumGray"
        borderRadius="xl"
        px="4"
        minH="12"
        mb="5"
        boxShadow="0 2px 12px rgba(0,0,0,0.06)"
      >
        <Box as="span" color="volyx.darkGray" flexShrink={0} mr="3" display="inline-flex">
          <Search size={20} aria-hidden />
        </Box>
        <Input
          type="search"
          aria-label="Search events and organizations"
          flex="1"
          border="none"
          bg="transparent"
          fontSize="md"
          fontFamily="body"
          color="volyx.text"
          outline="none"
          minW="0"
          _placeholder={{ color: 'volyx.darkGray' }}
          placeholder="Search events and organizations…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
        />
      </Flex>

      <VolunteersTabs
        tabs={[...marketplaceTabs]}
        activeTab={activeTab}
        onChange={(id) => setActiveTab(id as MarketplaceTab)}
      />

      <Box>
        {activeTab === 'events' &&
          (loadingEvents ? (
            <Text fontSize="sm" color="volyx.textSecondary" textAlign="center">
              Loading events…
            </Text>
          ) : filteredEvents.length === 0 ? (
            <Text fontSize="sm" color="volyx.textSecondary" textAlign="center">
              No events match your search.
            </Text>
          ) : (
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap="3">
              {filteredEvents.map((event) => {
                const dateLabel = formatDateRange(event.start_date ?? null, event.end_date ?? null);
                const joinCta = getEventJoinCta(event);
                return (
                  <Box
                    as="article"
                    key={event.id}
                    bg="white"
                    borderRadius="24px"
                    border="1px solid"
                    borderColor="volyx.mediumGray"
                    boxShadow="0 2px 10px rgba(0,0,0,0.08)"
                    overflow="hidden"
                    transition="all 0.2s"
                    _hover={{ boxShadow: '0 6px 18px rgba(0,0,0,0.12)' }}
                    cursor="pointer"
                    onClick={() => onEventPress(event)}
                  >
                    <Box position="relative" w="full" h="120px" overflow="hidden" bg="volyx.lightGray">
                      <EventImage
                        stored={event.image}
                        w="full"
                        h="full"
                        objectFit="cover"
                        transition="transform 0.3s"
                        _hover={{ transform: 'scale(1.05)' }}
                      />
                      {dateLabel ? (
                        <Box
                          position="absolute"
                          left="3"
                          top="3"
                          bg="volyx.primary"
                          color="white"
                          fontSize="xs"
                          fontWeight="semibold"
                          px="2.5"
                          py="1"
                          borderRadius="16px"
                          fontFamily="body"
                          zIndex={1}
                        >
                          {dateLabel}
                        </Box>
                      ) : null}
                    </Box>

                    <Flex direction="column" gap="2" px="3" pt="2.5" pb="3">
                      <Box minW="0">
                        <Text
                          as="h3"
                          m="0"
                          fontSize="sm"
                          fontWeight="semibold"
                          color="volyx.text"
                          fontFamily="body"
                          lineHeight="tight"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {event.name}
                        </Text>
                        {event.location ? (
                          <Flex align="center" gap="1" mt="1">
                            <Box as={MapPin} boxSize="13px" color="volyx.textSecondary" flexShrink={0} />
                            <Text
                              as="span"
                              fontSize="xs"
                              color="volyx.textSecondary"
                              fontFamily="body"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {event.location}
                            </Text>
                          </Flex>
                        ) : null}
                      </Box>
                      <chakra.button
                        type="button"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          if (!joinCta.disabled) onEventPress(event);
                        }}
                        w="full"
                        py="2"
                        px="4"
                        borderRadius="16px"
                        fontSize="sm"
                        fontWeight="semibold"
                        fontFamily="body"
                        cursor={joinCta.disabled ? 'default' : 'pointer'}
                        border="none"
                        bg={
                          joinCta.tone === 'primary'
                            ? 'volyx.primary'
                            : joinCta.tone === 'applied'
                              ? 'volyx.mediumGray'
                              : 'green.500'
                        }
                        color="white"
                        _hover={joinCta.disabled ? {} : { opacity: 0.9 }}
                        transition="opacity 0.2s"
                        _focus={{ outline: 'none', boxShadow: '0 0 0 2px #1F2932' }}
                        disabled={joinCta.disabled}
                        aria-label={joinCta.label}
                      >
                        {joinCta.label}
                      </chakra.button>
                    </Flex>
                  </Box>
                );
              })}
            </Grid>
          ))}

        {activeTab === 'organizations' &&
          (loadingOrganizations ? (
            <Text fontSize="sm" color="volyx.textSecondary" textAlign="center">
              Loading organizations…
            </Text>
          ) : filteredOrgs.length === 0 ? (
            <Text fontSize="sm" color="volyx.textSecondary" textAlign="center">
              No organizations match your search.
            </Text>
          ) : (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap="3" w="full">
              {filteredOrgs.map((org) => {
                const name = orgDisplayName(org);
                const status = org.membershipStatus;
                const busy = joiningOrgId === org.id;
                const actionLabel =
                  status === 'accepted'
                    ? 'Joined'
                    : status === 'pending'
                      ? 'Pending'
                      : 'Join';
                const actionDisabled =
                  busy || status === 'accepted' || status === 'pending';

                return (
                  <Flex
                    key={org.id}
                    align="center"
                    gap="3"
                    minW="0"
                    w="full"
                    bg="white"
                    borderRadius="24px"
                    border="1px solid"
                    borderColor="volyx.mediumGray"
                    px="4"
                    py="3"
                    minH={MARKETPLACE_ROW_MIN_H}
                    lineHeight="1.25"
                    boxShadow="0 2px 10px rgba(0,0,0,0.06)"
                    transition="box-shadow 0.2s"
                    _hover={{ boxShadow: '0 6px 18px rgba(0,0,0,0.12)' }}
                  >
                    <chakra.button
                      type="button"
                      flex="1"
                      minW="0"
                      display="flex"
                      alignItems="center"
                      gap="3"
                      textAlign="left"
                      border="none"
                      bg="transparent"
                      p="0"
                      m="0"
                      cursor="pointer"
                      lineHeight="1.25"
                      aria-label={`Organization profile: ${name}`}
                      onClick={() => setSelectedOrgForProfile(org)}
                    >
                      <Box
                        w="56px"
                        h="56px"
                        borderRadius="xl"
                        overflow="hidden"
                        flexShrink={0}
                        bg="volyx.lightGray"
                      >
                        <chakra.img
                          src={org.profile_image || DEFAULT_PROFILE_IMAGE}
                          alt=""
                          w="full"
                          h="full"
                          objectFit="cover"
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            const t = e.target as HTMLImageElement;
                            if (t.src !== DEFAULT_PROFILE_IMAGE) t.src = DEFAULT_PROFILE_IMAGE;
                          }}
                        />
                      </Box>
                      <Box minW="0" flex="1">
                        <Flex align="center" gap="1.5" mb="0.5">
                          <Building2 size={14} color="#6B7280" aria-hidden />
                          <Text fontWeight="semibold" fontSize="sm" color="volyx.text" lineClamp={1}>
                            {name}
                          </Text>
                        </Flex>
                        {org.location ? (
                          <Text fontSize="xs" color="volyx.textSecondary" lineClamp={1}>
                            {org.location}
                          </Text>
                        ) : null}
                      </Box>
                    </chakra.button>
                    <chakra.button
                      type="button"
                      flexShrink={0}
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      px="3"
                      py="2"
                      borderRadius="xl"
                      fontSize="xs"
                      fontWeight="semibold"
                      lineHeight="1.2"
                      bg={status === 'accepted' ? 'green.500' : 'volyx.primary'}
                      color="white"
                      opacity={actionDisabled ? 0.85 : 1}
                      cursor={actionDisabled ? 'default' : 'pointer'}
                      disabled={actionDisabled}
                      _hover={actionDisabled ? {} : { bg: 'rgba(31,41,50,0.9)' }}
                      onClick={() => {
                        if (!actionDisabled) void handleJoinOrg(org.id);
                      }}
                    >
                      {busy ? '…' : actionLabel}
                    </chakra.button>
                  </Flex>
                );
              })}
            </Grid>
          ))}
      </Box>
      <MarketplaceOrganizationProfileModal
        organization={selectedOrgForProfile}
        onClose={() => setSelectedOrgForProfile(null)}
        volunteerUserId={volunteerUserId}
        onEventPress={onEventPress}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    </Box>
  );
}
